/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import { CSSProperties, useCallback, useMemo, useRef } from 'react';
import { selectHeatMapsEnabled, selectRlFlToggleState } from '../../../store';
import { useSelectorWithMapId } from './BaseMapContainer';
import { useRouteMatch } from 'react-router-dom';
import MenuItems from '../layout/menu/MenuItems';
import { Feature, FeatureCollection } from '@turf/helpers';
import { MultiPolygon, Polygon } from 'geojson';
import { MapType, Network } from '../../../shared/entities/beam';
import { RlFlToggleStateEnum } from '../../../store/maps/reducer';
import {
  darkTheme,
  disableBeamContourHoverForVGNET,
  disableBeamCutoutForHnaAndSameBeam,
  disableBeamHoverForAllContoursBesidesVGNET,
  performCutout,
} from '../../../utils/BeamUtils';
import { GeoJsonLayer } from '@deck.gl/layers';
import { Color } from '@deck.gl/core';
import chroma from 'chroma-js';

export type BeamLayerConfig = {
  data: FeatureCollection<MultiPolygon | Polygon>;
  id?: string;
  linkType?: 'rl' | 'fl';
  blendColor?: any;
  blendOpacity?: number;
  fillColor?: number[] | ((feature: any) => number[]);
  lineColor?: number[] | ((feature: any) => number[]);
  highlightColor?: number[] | ((feature: any) => number[]);
  hidden?: boolean;
  tooltipStyle?: CSSProperties;
  disableTooltip?: boolean;
  disableBeamHover?: boolean | ((networkType: Network, mapType: MapType) => boolean);
  disableBeamContourHover?: boolean | ((networkType: Network, mapType: MapType) => boolean);
  tooltipText?: (feature: any) => string;
  featureSortFunction?: (
    feature1: Feature<Polygon | MultiPolygon>,
    feature2: Feature<Polygon | MultiPolygon>,
  ) => number;
  disableBeamCutoutForFeaturePairs?: (
    feature1: Feature<Polygon | MultiPolygon>,
    feature2: Feature<Polygon | MultiPolygon>,
  ) => boolean;
  isCutoutEnabled?: boolean;
};

export type HoverData = {
  hoveredObject: any;
  pointerX: number;
  pointerY: number;
};

type BeamLayerOutput = {
  geoJsonLayers: GeoJsonLayer[];
  hoverDataEventTarget: EventTarget;
};

/**
 * Custom hook to dynamically create multiple Deck.gl GeoJsonLayers to be rendered on the map.
 *
 * @param props - Data and configruation for the Deck.gl GeoJsonLayers
 * @returns An object containing the Deck.gl GeoJsonLayers and an EventTarget that receives
 * events when the hover data changes, causing the corresponding tooltip to re-render.
 */
export const useBeamGeoJsonLayers = (props: BeamLayerConfig): BeamLayerOutput => {
  const rlFlToggleState = useSelectorWithMapId(selectRlFlToggleState);
  const heatMapsEnabled = useSelectorWithMapId(selectHeatMapsEnabled);
  const isExportView = useRouteMatch(`/${MenuItems.fleet}/:datapathUuid/export`);

  const {
    data,
    id = 'geojson-map-controller',
    lineColor = darkTheme.lineColor,
    highlightColor = darkTheme.highlightColor,
    fillColor = darkTheme.fillColor,
    disableBeamHover = heatMapsEnabled ? disableBeamHoverForAllContoursBesidesVGNET : true,
    disableBeamContourHover = heatMapsEnabled ? disableBeamContourHoverForVGNET : false,
    isCutoutEnabled = heatMapsEnabled && isExportView,
    featureSortFunction = defaultFeatureSortFunction,
    disableBeamCutoutForFeaturePairs = disableBeamCutoutForHnaAndSameBeam,
    blendOpacity = 0.382,
    blendColor = '#3e4f60',
    linkType = rlFlToggleState === RlFlToggleStateEnum.ReturnLink ? 'rl' : 'fl',
    hidden = false,
  } = props;

  // EventTarget to notify tooltip component when hover data changes and what that data is.
  // Prevents unnecessary re-renders of the parent component (ie MapOverlays) when hover data changes.
  // Keep a reference to this so that on re-render, we do not dispatch to a stale object
  const hoverDataEventTarget = useRef<EventTarget>(new EventTarget());

  const deckLayersCallback = useCallback(
    (
      layerId: string,
      sortFunc?: (feature1: Feature<Polygon | MultiPolygon>, feature2: Feature<Polygon | MultiPolygon>) => number,
      disableBeamCutoutFunc?: (
        feature1: Feature<Polygon | MultiPolygon>,
        feature2: Feature<Polygon | MultiPolygon>,
      ) => boolean,
    ) => {
      let featureCollectionData = data;

      if (isCutoutEnabled) {
        featureCollectionData = performCutout(featureCollectionData, sortFunc, disableBeamCutoutFunc);
      }
      // TODO: Build this as a map where the keys are the beam name and the values is an object with 2 propeties, the min and max, so that the saturation of the contours can be relative to the beam itself
      const lowestBps = data.features.reduce(
        (lowestRate: number, currentFeature: Feature<MultiPolygon | Polygon>) =>
          (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) <
          lowestRate
            ? linkType === 'rl'
              ? currentFeature.properties?.rlDataRateBps
              : currentFeature.properties?.flDataRateBps
            : lowestRate,
        Infinity,
      );
      const greatestBps = data.features.reduce(
        (greatestRate: number, currentFeature: Feature<MultiPolygon | Polygon>) =>
          (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) >
          greatestRate
            ? linkType === 'rl'
              ? currentFeature.properties?.rlDataRateBps
              : currentFeature.properties?.flDataRateBps
            : greatestRate,
        -Infinity,
      );

      const colorAlgorithm = (color: any, bps: number, min: any, max: any, enableBlend: boolean): Color => {
        const gradientStopColor = chroma(color.slice(0, 3)).alpha(1).darken(4).saturate(3);
        const colorScale = chroma.scale([chroma(color.slice(0, 3)), gradientStopColor]).domain([min, max]);
        const mixedColor = colorScale(bps);
        let finalColor = [...mixedColor.rgb(), color[3]] as Color;
        if (enableBlend && blendColor) {
          const mixedToAverageMapBackgroundColorScale = chroma.scale([chroma(blendColor).alpha(1), mixedColor]);
          const mixedColorBlended = mixedToAverageMapBackgroundColorScale(blendOpacity);
          finalColor = [...mixedColorBlended.rgb(), 255];
        }

        return finalColor;
      };
      const highlightFunc = (picked: any) => {
        const { object: feature } = picked;
        if (!feature) {
          return [0, 0, 0, 0] as number[];
        }
        const color = highlightColor instanceof Function ? highlightColor(feature) : highlightColor;
        const bps = linkType === 'rl' ? feature.properties.rlDataRateBps : feature.properties.flDataRateBps;

        if (!bps) {
          return color;
        }
        return colorAlgorithm(color, bps, lowestBps, greatestBps, true) as number[];
      };
      const colorFunc = (colorType: number[] | ((feature: any) => number[])) => (feature: any) => {
        const color = colorType instanceof Function ? (colorType(feature) as Color) : (colorType as Color);
        const bps = linkType === 'rl' ? feature.properties.rlDataRateBps : feature.properties.flDataRateBps;

        if (!bps) {
          return color;
        }

        return colorAlgorithm(color, bps, lowestBps, greatestBps, false);
      };

      let contourFeatureCollections = [featureCollectionData];
      let contourLayers = [];
      let utilizeNetworkAndMapType = false;

      // use contour type hover functionality if provided
      if (disableBeamHover instanceof Function || disableBeamContourHover instanceof Function) {
        const networkTypeToFeatureMap: { [key: string]: Feature<Polygon | MultiPolygon>[] } = {};
        utilizeNetworkAndMapType = true;

        featureCollectionData.features.forEach((feature: Feature<Polygon | MultiPolygon>) => {
          const key = feature.properties?.network;
          if (!networkTypeToFeatureMap[key]) {
            networkTypeToFeatureMap[key] = [];
          }

          networkTypeToFeatureMap[key].push(feature);
        });

        contourFeatureCollections = Object.keys(networkTypeToFeatureMap).map(
          cType =>
            ({ type: 'FeatureCollection', features: networkTypeToFeatureMap[cType] } as FeatureCollection<
              Polygon | MultiPolygon
            >),
        );
      }

      contourLayers = contourFeatureCollections.reduce(
        (accum: GeoJsonLayer[], featureCollection: FeatureCollection<Polygon | MultiPolygon>) => {
          const networkTypeForFeatureCollection = utilizeNetworkAndMapType
            ? featureCollection.features[0].properties?.network
            : '';
          const mapTypeForFeatureCollection = utilizeNetworkAndMapType
            ? featureCollection.features[0].properties?.mapType
            : '';

          // polygon layer
          accum.push(
            new GeoJsonLayer({
              id: `${layerId}-${networkTypeForFeatureCollection}polygons`,
              data: featureCollection,

              // Styles
              widthUnits: 'pixels',
              lineWidthScale: 1,
              lineWidthMinPixels: 1,
              stroked: true,
              filled: true,
              visible: !hidden,

              // Interactive props
              pickable:
                disableBeamHover instanceof Function
                  ? !disableBeamHover(networkTypeForFeatureCollection, mapTypeForFeatureCollection)
                  : !disableBeamHover,
              onHover: (feature: any) => {
                // Dispatch an event when hover data changes
                hoverDataEventTarget.current.dispatchEvent(
                  new CustomEvent<HoverData>(HoverDataChangedEvent, {
                    detail: {
                      hoveredObject: feature.object || {},
                      pointerX: feature.x || 0,
                      pointerY: feature.y || 0,
                    },
                  }),
                );
              },
              autoHighlight: true,

              getFillColor: colorFunc(fillColor),
              getLineColor: colorFunc(lineColor),
              highlightColor: highlightFunc,
            }),
          );

          // polygon contour layer
          accum.push(
            new GeoJsonLayer({
              id: `${layerId}-${networkTypeForFeatureCollection}contourLines`,
              data: featureCollection,
              // Styles
              widthUnits: 'pixels',
              lineWidthScale: 1,
              lineWidthMinPixels: 3,
              stroked: true,
              filled: false,
              visible: !hidden,

              // Interactive props
              pickable:
                disableBeamContourHover instanceof Function
                  ? !disableBeamContourHover(networkTypeForFeatureCollection, mapTypeForFeatureCollection)
                  : !disableBeamContourHover,
              autoHighlight: true,
              getLineColor: [0, 0, 0, 0] as Color,
              highlightColor: highlightFunc,
              // Interactive props
              onHover: (feature: any) => {
                // Dispatch an event when hover data changes
                hoverDataEventTarget.current.dispatchEvent(
                  new CustomEvent<HoverData>(HoverDataChangedEvent, {
                    detail: {
                      hoveredObject: feature.object || {},
                      pointerX: feature.x || 0,
                      pointerY: feature.y || 0,
                    },
                  }),
                );
              },
            }),
          );
          return accum;
        },
        [],
      );
      return contourLayers;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableBeamContourHover, disableBeamHover, isCutoutEnabled, linkType, hidden, data],
  );

  const geoJsonLayers = useMemo(() => {
    let layers: GeoJsonLayer[] = [];

    if (window['google'] && data.features) {
      layers = deckLayersCallback(id, featureSortFunction, disableBeamCutoutForFeaturePairs);
    }

    return layers;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckLayersCallback]);

  return { geoJsonLayers, hoverDataEventTarget: hoverDataEventTarget.current };
};

const defaultFeatureSortFunction = (
  feature1: Feature<Polygon | MultiPolygon>,
  feature2: Feature<Polygon | MultiPolygon>,
) => (feature1.properties && feature2.properties ? feature1.properties.priority - feature2.properties.priority : 0);

export const HoverDataChangedEvent = 'HoverDataChangedEvent';
